/**
 * GoogleConnectHeader — dashboard-level Google integrations status.
 *
 * Renders a compact pill in the header that opens into a full "what's
 * connected & what it does" panel: connected email, scopes granted
 * (Contacts / Calendar), an auto-push toggle for new leads, a bulk
 * push-all-leads action, and disconnect. If the dealer connected under
 * the old Contacts-only scope, a "Reconnect for Calendar" banner
 * appears so they can grant the widened scope without losing state.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, LogIn, Check, X, Users, Calendar, Zap, Upload } from 'lucide-react';
import { getAuthToken } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleConnectHeader = ({ isDark }) => {
  const [state, setState] = useState({
    loading: true, connected: false, email: null,
    hasContacts: false, hasCalendar: false,
    contactCount: 0, calendarCount: 0,
    autoPushContacts: false,
  });
  const [busy, setBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const token = getAuthToken();
      const r = await axios.get(`${API}/google/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setState({
        loading: false,
        connected: !!r.data?.connected,
        email: r.data?.email || null,
        hasContacts: !!r.data?.has_contacts,
        hasCalendar: !!r.data?.has_calendar,
        contactCount: r.data?.contact_count || 0,
        calendarCount: r.data?.calendar_count || 0,
        autoPushContacts: !!r.data?.auto_push_contacts,
      });
    } catch {
      setState((s) => ({ ...s, loading: false, connected: false, email: null }));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Toast + refresh once the Google OAuth result lands — either still in
  // the URL (?google=…) or stashed in sessionStorage by the dashboard
  // before it stripped the params during its early-mount race.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let g = params.get('google');
    let msg = params.get('msg') || '';
    if (g) {
      params.delete('google'); params.delete('msg');
      const nu = window.location.pathname + (params.toString() ? `?${params}` : '');
      window.history.replaceState({}, '', nu);
    } else {
      const stashed = sessionStorage.getItem('google_oauth_result');
      if (stashed) {
        try { ({ status: g, msg } = JSON.parse(stashed)); } catch { g = null; }
      }
    }
    if (!g) return;
    sessionStorage.removeItem('google_oauth_result');
    if (g === 'connected') {
      toast.success('Google connected — pick what to sync in the popup', { duration: 8000 });
      refresh();
      // Auto-open the panel so the user immediately sees exactly what
      // just got connected + can flip on auto-sync in one tap. Prior
      // behavior was silent post-redirect which felt like nothing
      // happened.
      setMenuOpen(true);
    } else if (g === 'error') {
      toast.error(`Google connect failed: ${msg || 'unknown'}`, { duration: 10000 });
    }
  }, [refresh]);

  const connect = async () => {
    setBusy(true);
    try {
      const token = getAuthToken();
      const r = await axios.get(`${API}/google/connect`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.data?.authorization_url) window.location.href = r.data.authorization_url;
      else toast.error('Could not start Google connect.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Google connect failed');
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google? Your existing pushed contacts and calendar events stay in Google; RV Match just stops syncing new ones.')) return;
    setBusy(true);
    try {
      const token = getAuthToken();
      await axios.post(`${API}/google/disconnect`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Google disconnected');
      setMenuOpen(false);
      refresh();
    } catch {
      toast.error('Disconnect failed');
    } finally { setBusy(false); }
  };

  const toggleAutoPush = async () => {
    setAutoBusy(true);
    const next = !state.autoPushContacts;
    try {
      const token = getAuthToken();
      await axios.post(`${API}/google/auto-push`, { enabled: next }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setState((s) => ({ ...s, autoPushContacts: next }));
      toast.success(next ? 'Auto-push enabled — new leads will sync to Google Contacts.' : 'Auto-push disabled.');
    } catch {
      toast.error('Could not update auto-push setting');
    } finally { setAutoBusy(false); }
  };

  const bulkPush = async () => {
    if (!window.confirm('Push every existing lead you haven\'t already exported to Google Contacts? Each will be tagged "RV Match A.I. Lead".')) return;
    setBulkBusy(true);
    try {
      const token = getAuthToken();
      const r = await axios.post(`${API}/leads/bulk-push-google`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { pushed, skipped_already_exported: skipped, failed } = r.data;
      const parts = [];
      if (pushed) parts.push(`${pushed} pushed`);
      if (skipped) parts.push(`${skipped} already synced`);
      if (failed) parts.push(`${failed} failed`);
      toast.success(parts.join(' · ') || 'No leads to push');
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk push failed');
    } finally { setBulkBusy(false); }
  };

  if (state.loading) return null;

  if (!state.connected) {
    return (
      <button
        onClick={connect}
        disabled={busy}
        className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
          isDark
            ? 'bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 border border-white/10'
            : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'
        }`}
        data-testid="header-google-connect-btn"
        title={
          'Connect your Google account so RV Match can:\n' +
          '  • Push every new lead to Google Contacts (tagged "RV Match A.I. Lead")\n' +
          '  • Auto-create Google Calendar events for every appointment\n' +
          '  • Bulk-sync all existing leads with one click\n' +
          '\nRV Match never emails on your behalf, and you can disconnect any time.'
        }
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <LogIn size={12} />}
        Connect Google
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setMenuOpen((v) => !v)}
        className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${
          isDark ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/30' : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
        }`}
        data-testid="header-google-connected-pill"
        title={state.email ? `Google: ${state.email}` : 'Google connected'}
      >
        <Check size={12} />
        Google
      </button>
      {menuOpen && (
        <div
          className={`absolute right-0 mt-2 w-80 rounded-xl border shadow-xl z-40 p-3 ${isDark ? 'bg-slate-900 border-slate-700 text-slate-200' : 'bg-white border-slate-200 text-slate-800'}`}
          data-testid="header-google-menu"
        >
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-60">Google Integrations</p>
            <button
              onClick={() => setMenuOpen(false)}
              className="opacity-60 hover:opacity-100"
              aria-label="Close"
            >
              <X size={12} />
            </button>
          </div>
          <p className={`text-sm font-semibold truncate mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{state.email || 'Connected'}</p>

          {/* Scope status rows */}
          <div className="mt-3 space-y-1.5">
            <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              <Users size={13} className={state.hasContacts ? 'text-emerald-500' : 'text-slate-400'} />
              <span className="flex-1">
                <strong>Contacts</strong> — {state.hasContacts ? `${state.contactCount} synced` : 'not granted'}
              </span>
              {state.hasContacts ? <Check size={12} className="text-emerald-500" /> : null}
            </div>
            <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              <Calendar size={13} className={state.hasCalendar ? 'text-emerald-500' : 'text-slate-400'} />
              <span className="flex-1">
                <strong>Calendar</strong> — {state.hasCalendar ? `${state.calendarCount} events created` : 'not granted'}
              </span>
              {state.hasCalendar ? <Check size={12} className="text-emerald-500" /> : null}
            </div>
          </div>

          {/* Reconnect nudge when the older Contacts-only scope is stored. */}
          {!state.hasCalendar && (
            <button
              onClick={connect}
              className={`mt-2 w-full text-left text-[11px] rounded-lg px-2.5 py-2 font-semibold ${isDark ? 'bg-amber-500/10 text-amber-300 border border-amber-400/30' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}
              data-testid="header-google-reconnect-calendar"
            >
              Reconnect to also grant Calendar access →
            </button>
          )}

          {/* Auto-push toggle */}
          <div className={`mt-3 flex items-center gap-2 rounded-lg p-2 ${isDark ? 'bg-slate-800/60' : 'bg-slate-50'}`}>
            <Zap size={13} className={state.autoPushContacts ? 'text-emerald-500' : 'text-slate-400'} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold">Auto-sync new leads</p>
              <p className={`text-[10px] leading-tight ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Every lead you get is pushed to Google Contacts automatically.
              </p>
            </div>
            <button
              onClick={toggleAutoPush}
              disabled={autoBusy}
              role="switch"
              aria-checked={state.autoPushContacts}
              className={`shrink-0 relative w-9 h-5 rounded-full transition-colors ${state.autoPushContacts ? 'bg-emerald-500' : (isDark ? 'bg-slate-600' : 'bg-slate-300')}`}
              data-testid="header-google-auto-push-toggle"
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${state.autoPushContacts ? 'translate-x-4' : ''}`}
              />
            </button>
          </div>

          {/* Bulk push */}
          <button
            onClick={bulkPush}
            disabled={bulkBusy}
            className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold ${isDark ? 'bg-white/[0.06] hover:bg-white/[0.1] text-slate-100 border border-white/10' : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200'}`}
            data-testid="header-google-bulk-push"
          >
            {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            Push all leads now
          </button>

          <p className={`mt-2 text-[10px] leading-snug ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
            Every synced contact is tagged <strong>&ldquo;RV Match A.I. Lead&rdquo;</strong> as a userDefined field and in the note, so you can spot them at a glance in Google Contacts.
          </p>

          <button
            onClick={disconnect}
            disabled={busy}
            className={`mt-3 w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold ${isDark ? 'bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 border border-rose-400/30' : 'bg-rose-100 text-rose-700 hover:bg-rose-200 border border-rose-200'}`}
            data-testid="header-google-disconnect-btn"
          >
            <X size={12} /> Disconnect Google
          </button>
        </div>
      )}
    </div>
  );
};

export default GoogleConnectHeader;
